import {
  createSlice,
  createSelector,
  createListenerMiddleware,
} from "@reduxjs/toolkit";
import { getPreventiveAppointmentsApi, retrieveAppointmentStatusForEditAPI } from "src/api/preventiveAppointmentAPI";
import {
  IPreventiveAppointment,
  IPreventiveAppointmentData,
} from "../interfaces/IPreventiveAppointment";
import { trackPromise } from "react-promise-tracker";
import {
  toCamel,
  formatDateV2,
  toPascal,
  getTextValueById,
  convertToISO,
  isCivilDefenseUser,
} from "../../utils/helpers";
import { handleResponse } from "src/services/apiServices";
import { NUMBER_OF_ROWS_PER_PAGE } from "src/resources/constant";

export const getPreventiveAppointment = (forExport = false) => {
  return async (dispatch: any, getState: any) => {
    const {
      lookup: { serviceTerritories },
      preventiveAppointments: {
        filterData,
        gridCurrentPage,
        numberOfRowsPerPage,
      },
    } = getState();

    let { startAppointmentDate, endAppointmentDate } = filterData;
    const currentPage = gridCurrentPage + 1;
    const allCityTerritories = serviceTerritories.map(
      (item: any) => item.territoryId
    );

    let territories = filterData.territoryId;
    if (filterData.cityId && (!territories || territories.length === 0)) {
      territories = allCityTerritories;
    }
    const data = {
      ...toPascal({
        ...filterData,
        startAppointmentDate: convertToISO(startAppointmentDate),
        endAppointmentDate: convertToISO(endAppointmentDate),
      }),
      TerritoryId: isCivilDefenseUser() ? territories : filterData.territoryId,
      PageNumber: currentPage,
      PageCount: numberOfRowsPerPage,
    
    };

    if (forExport) {
      data.PageNumber = 1;
      data.PageCount = numberOfRowsPerPage * currentPage;
    }

    return await trackPromise(
      getPreventiveAppointmentsApi(data)
        .then((res) => {
          dispatch(setTotalRowsCount(res?.data?.TotalRowsCount || 0));
          const data = toCamel(res?.data?.AppointmentInfo || []);

          const { lookup } = getState();
          const {
            appointmentsStatuses,
            onHoldReasons,
            allServiceTerritories,
            appointmentTypes,
          } = lookup;
          const mappedData = data.map((item: any) => {
            const status = appointmentsStatuses?.find(
              (x: { id: number }) => x.id == item.appointmentEntityInfo.statusId
            );
            const onHoldReason = onHoldReasons?.find(
              (x: { id: number }) =>
                x.id == item.appointmentEntityInfo.onHoldReasonId
            );
            const serviceTerritory = allServiceTerritories?.find(
              (x: { id: number }) =>
                x.id == item.appointmentEntityInfo.serviceTerritoryId
            );

            const appointmentType = appointmentTypes?.find(
              (x: { id: number }) =>
                x.id == item.appointmentEntityInfo.appointmentTypeId
            );

            return {
              appointmentId: item.appointmentEntityInfo.appointmentId,
              appointmentIdNumber:
                item.appointmentEntityInfo.appointmentIdNumber,
              appointmentDate: formatDateV2(
                item.appointmentEntityInfo.appointmentDate
              ),
              appointmentTypeEn: appointmentType?.englishName,
              appointmentTypeAr: appointmentType?.arabicName,
              completionDate: formatDateV2(
                item.appointmentEntityInfo.completiondate
              ),
              creationDate: formatDateV2(
                item.appointmentEntityInfo.creationDate
              ),
              onHoldReasonId: item.appointmentEntityInfo.onHoldReasonId,
              onHoldReasonEn: onHoldReason?.englishName,
              onHoldReasonAr: onHoldReason?.arabicName,
              statusEn: status?.englishName,
              statusAr: status?.arabicName,
              statusId: item.appointmentEntityInfo.statusId,
              propertyId: item.buildingEntityInfo.propertyId,
              longitude: item.buildingEntityInfo.address1_Longitude,
              latitude: item.buildingEntityInfo.address1_Latitude,
              buildingName: item.buildingEntityInfo.name,
              sLABreached: item.appointmentEntityInfo.sLABreached,
              propertyNumber: item.buildingEntityInfo.propertyNumber,
              managerSupervisorName:
                item.appointmentEntityInfo.managerSupervisorName,
              territory: getTextValueById(
                item.buildingEntityInfo.serviceTerritoryId,
                allServiceTerritories,
                "territoryId"
              ),
              companyName: item.appointmentEntityInfo.companyName,
              serviceTerritoryId: item.buildingEntityInfo.serviceTerritoryId,
              city: item.buildingEntityInfo.city || "",
              emirate: item.buildingEntityInfo.emirate || "",
              PRPNumber:item.buildingEntityInfo.pRPNumber,
              PropertyTradeLicense:item.buildingEntityInfo.propertyTradeLicense,
            };
          });
          if (forExport) {
            dispatch(setExportData(mappedData));
          } else {
            dispatch(setPreventiveAppointments(mappedData));
          }
        })
        .catch((err) => {
          //  onFailure?.(err);
          handleResponse(err);
        })
    );
  };
};
export const retrieveAppointmentStatusForEdit = () => {
  return async (dispatch: any, getState: any) => {
    await retrieveAppointmentStatusForEditAPI().then((response) => {
      dispatch(setAppointmentStatusForEdit(toCamel(response.data.OptionSetItems)) as any);
    });
  };
};
const initialState: IPreventiveAppointment = {
  preventiveAppointmentData: [],
  isPreventiveAppointmentDetailsPopupOpen: false,
  preventiveAppointmentDetailsPopupData: {
    appointmentId: 0,
    status: "",
    statusReason: "",
  },
  preventiveAppointmentSearchText: "",
  filterData: {
    propertyId: "",
    startAppointmentDate: null,
    endAppointmentDate: null,
    serviceTerritory: "",
    status: null,
    cityId: "",
    territoryId: "",
    pageNumber: 1,
    pageCount: 20, //set it into config
    slaBreached: null,
    contractNumber: "",
    company: "",
    sortColumn: "appointmentDate",
    isAscending: true,
    showHistoricalData: false,
    PRPNumber:"",
    PropertyTradeLicense:"",
    
  },
  totalRowsCount: 0,
  gridCurrentPage: 0,
  numberOfRowsPerPage: NUMBER_OF_ROWS_PER_PAGE,
  exportData: [],
  appointmentStatusForEdit:[],
};

const preventiveAppointmentsSlice = createSlice({
  name: "preventiveAppointments",
  initialState,
  reducers: {
    setPreventiveAppointments: (state, action) => {
      state.preventiveAppointmentData = [...action.payload];
    },
    setAppointmentStatus: (state, action) => {
      const preventiveAppointmentsData = state.preventiveAppointmentData.map(
        (preventiveAppointment: any) => {
          return preventiveAppointment;
        }
      );
      state.preventiveAppointmentData = preventiveAppointmentsData;
    },
    setGridCurrentPage: (state, action) => {
      state.gridCurrentPage = action.payload;
    },
    setIsPreventiveAppointmentDetailsPopupOpen: (state, action) => {
      state.isPreventiveAppointmentDetailsPopupOpen = action.payload;
    },
    setTotalRowsCount: (state, action) => {
      state.totalRowsCount = action.payload;
    },
    setPreventiveAppointmentPopupData: (
      state,
      action: {
        payload: { field: string; value: string | number };
        type: string;
      }
    ): void => {
      state.preventiveAppointmentDetailsPopupData = {
        ...state.preventiveAppointmentDetailsPopupData,
        [action.payload.field]: action.payload.value,
      };
    },
    clearPreventiveAppointmentPopupData: (state) => {
      state.preventiveAppointmentDetailsPopupData =
        initialState.preventiveAppointmentDetailsPopupData;
    },
    setPreventiveAppointmentSearchText: (state, action) => {
      state.preventiveAppointmentSearchText = action.payload;
    },
    setFilterDataField: (state, action) => {
      state.filterData = {
        ...state.filterData,
        [action.payload.field]: action.payload.value,
      };
    },
    resetFilterData: (state) => {
      state.filterData = initialState.filterData;
      state.gridCurrentPage = initialState.gridCurrentPage;
    },
    setNumberOfRowsPerPage: (state, action) => {
      state.numberOfRowsPerPage = action.payload;
    },
    setExportData: (state, action) => {
      state.exportData = action.payload;
    },
    setAppointmentStatusForEdit: (state, action) => {
      state.appointmentStatusForEdit = action.payload;
    },
  },
});

export const {
  setPreventiveAppointments,
  setAppointmentStatus,
  setPreventiveAppointmentSearchText,
  setIsPreventiveAppointmentDetailsPopupOpen,
  setPreventiveAppointmentPopupData,
  setTotalRowsCount,
  clearPreventiveAppointmentPopupData,
  resetFilterData,
  setFilterDataField,
  setGridCurrentPage,
  setNumberOfRowsPerPage,
  setExportData,
  setAppointmentStatusForEdit
} = preventiveAppointmentsSlice.actions;
export default preventiveAppointmentsSlice.reducer;

const makeFilterAppointmentsSelector = (
  dataSourceSelector: (state: any) => any
) =>
  createSelector(
    dataSourceSelector,
    (state: any) =>
      state.preventiveAppointments.preventiveAppointmentSearchText,
    (preventiveAppointmentData, preventiveAppointmentSearchText) => {
      return getPreventiveAppointmentsFilteredData(
        preventiveAppointmentData,
        preventiveAppointmentSearchText
      );
    }
  );

export const selectPreventiveAppointments = makeFilterAppointmentsSelector(
  (state: any) => state.preventiveAppointments.preventiveAppointmentData
);

export const selectPreventiveAppointmentsForExport =
  makeFilterAppointmentsSelector(
    (state: any) => state.preventiveAppointments.exportData
  );

//listener that listens to setGridCurrentPage and dispatch getPreventiveAppointment
const getAppointmentsListenerMiddleware = createListenerMiddleware();

getAppointmentsListenerMiddleware.startListening({
  actionCreator: setGridCurrentPage,
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(getPreventiveAppointment());
  },
});

export { getAppointmentsListenerMiddleware };
function getPreventiveAppointmentsFilteredData(
  preventiveAppointmentData: any,
  preventiveAppointmentSearchText: any
): any {
  return preventiveAppointmentData.filter(
    (preventiveAppointment: IPreventiveAppointmentData) => {
      return Object.values(preventiveAppointment).some((field) => {
        return field
          ?.toString()
          ?.toLowerCase()
          ?.includes(preventiveAppointmentSearchText.toLowerCase());
      });
    }
  );
}
